<template>
  <div class="site-header-container">
    <header class="site-header">
      <div class="site-header__container container">
        <button
          class="site-header__menu icon-button"
          type="button"
          @click.prevent="handleMenuToggle"
        >
          <span
            class="visually-hidden"
            v-text="$string('general.site_header.menu')"
          />

          <icon-menu v-if="!isMenuDrawerOpen" />
          <icon-close v-else />
        </button>

        <!-- Logo -->
        <component
          :is="titleElement"
          class="site-header__logo"
        >
          <a :href="$variable('routes.root')">
            <span
              class="visually-hidden"
              v-text="$variable('store.shopName')"
            />

            <brand-logo />
          </a>
        </component>

        <ul class="site-header__nav">
          <template
            v-for="(linkList, index) of linkLists"
            :key="index"
          >
            <li v-if="linkList?.items?.length">
              <button
                :id="`header-nav-${linkList?.handle}`"
                type="button"
                class="site-header__nav-button text-body-s-bold"
                :class="{ [$class('active')]: activeLinkList?.handle === linkList.handle }"
                :aria-expanded="activeLinkList?.handle === linkList.handle"
                :aria-controls="`menu-${linkList?.handle}`"
                :aria-label="$string(
                  'general.site_header.navigation_toggle',
                  {
                    replace: { menu: linkList.title },
                  }
                )"
                @click="throttledToggleMenu(linkList)"
                @keyup.enter.space="throttledToggleMenu(linkList)"
                v-text="linkList.title"
              />
            </li>
          </template>
        </ul>

        <header-search v-if="!isMobile" />

        <div class="site-header__icons">
          <a
            class="site-header__account icon-button"
            :href="$variable('routes.account.url')"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.account')"
            />

            <icon-account />
          </a>

          <a
            class="site-header__cart icon-button"
            :href="$variable('routes.cart.url')"
            @click="handleCartToggle"
          >
            <span
              class="visually-hidden"
              v-text="$string('general.site_header.cart')"
            />

            <icon-cart />

            <span
              v-if="cart.item_count > 0"
              class="site-header__cart-badge critical-clear text-body-2xs-regular"
              aria-hidden="true"
              v-text="cart.item_count"
            />
          </a>
        </div>
      </div>
    </header>

    <!-- Search form for mobile -->
    <div
      v-if="isMobile"
      ref="searchForm"
      class="site-header__search-form-wrapper container"
      :class="{'faded': isMenuDrawerOpen}"
    >
      <header-search />
    </div>

    <overlay
      key="menu-drawer"
      block-class="menu-drawer-overlay"
      direction="right"
      namespace="menu-drawer"
      type="drawer"
      :show-close="false"
    >
      <template #body="overlay">
        <menu-drawer
          v-if="getComponentVisible('menu-drawer')"
          v-cloak
          :handle="mobile.handle"
          :contact-us-link="contactUsLink"
          :delivery-return-link="deliveryReturnLink"
          :item-count="mobile.itemCount"
          :link-lists="linkLists"
          :overlay="overlay"
        />

        <div
          v-if="!getComponentActive('menu-drawer')"
          class="navigation__menu-drawer"
        >
          <div
            v-for="(_, index) of [...Array(mobile.itemCount)]"
            :key="`menu-drawer-loading-${index}`"
            class="navigation__menu-drawer-link"
          >
            <span class="navigation__menu-drawer-loading" />
          </div>
        </div>
      </template>
    </overlay>

    <!-- Mega Navigation -->
    <overlay
      key="mega-nav"
      block-class="mega-nav-overlay"
      direction="none"
      namespace="mega-nav"
      :show-close="false"
      type="modal"
    >
      <template #body="overlay">
        <mega-nav
          v-if="activeLinkList"
          :active-link-list="activeLinkList"
          :overlay="overlay"
          @mega-nav-closed="activeLinkList = null"
        />
      </template>
    </overlay>
  </div>
</template>

<script>

/**
 * Vue: Site header (site-header)
 * -----------------------------------------------------------------------------
 * Site header central banner containing logo, icon buttons, and menu drawer.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} liquid - Liquid section ID, used for theme editor events.
 * @param {String} titleElement - HTML element of site logo based on page.
 *
 */
import { defineAsyncComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import { realThrottle as throttle } from '~/helpers/general'

import Overlay from '~global/overlay/overlay'
import HeaderSearch from '~async/algolia/header-search/header-search'
import MegaNav from '~async/mega-nav/mega-nav'

import BrandLogo from '~icons/general/brand-logo.svg'
import IconAccount from '~icons/general/account.svg'
import IconCart from '~icons/general/cart.svg'
import IconClose from '~icons/directional-navigation/close.svg'
import IconMenu from '~icons/general/menu.svg'
import IconChevronDown from '~icons/directional-navigation/chevron/down.svg'

export default {
  name: 'SiteHeader',

  components: {
    BrandLogo,
    IconAccount,
    IconCart,
    IconClose,
    IconMenu,
    IconChevronDown,
    Overlay,
    HeaderSearch,
    MegaNav,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
  },

  props: {
    // Needed for theme editor events
    // eslint-disable-next-line vue/no-unused-properties
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    contactUsLink: {
      type: String,
      default: '',
    },
    deliveryReturnLink: {
      type: String,
      default: '',
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
      }),
    },
    titleElement: {
      type: String,
      default: 'h1',
      validator: (value) => {
        return ['h1', 'span'].includes(value)
      },
    },
    linkLists: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      activeLinkList: null,
      isMenuDrawerOpen: false,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      isMobile: (state) => state.index.screen.mobile,
      throttleSetting: (state) => state.index.settings.throttle,
    }),
  },

  watch: {

    /**
     * Watch active overlay to hide/show search.
     */
    getActiveOverlay() {
      document.documentElement.style.setProperty(
        '--search-form-height',
        `${this.$refs?.searchForm?.offsetHeight - 1}px`,
      )

      if (this.getActiveOverlay?.namespace === 'menu-drawer') {
        this.isMenuDrawerOpen = true
        return
      }

      if (this.getActiveOverlay?.namespace === 'search-modal') {
        this.searchModalActive = true
        return
      }

      this.isMenuDrawerOpen = false
      this.searchModalActive = false
    },
  },

  mounted() {
    // Throttled toggleMenu function.
    this.throttledToggleMenu = throttle(this.toggleMenu, this.throttleSetting)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      toggleOverlay: 'overlays/toggle',
    }),

    /**
     * Handle menu toggle.
     */
    handleMenuToggle() {
      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menu-drawer',
      })
    },

    /**
     * Handle toggle menu.
     */
    toggleMenu(linkList) {
      if (!linkList?.items?.length) {
        this.closeOverlay('mega-nav')
        this.activeLinkList = null
        return
      }

      this.openOverlay({
        component: 'mega-nav',
        ignoreDismissed: true,
        namespace: 'mega-nav',
      })

      this.activeLinkList = linkList
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (!this.isMobile) {
        return
      }

      this.openOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menu-drawer',
      })
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      this.closeOverlay('menu-drawer')
    },
  },
}
</script>
